import { IItemData } from '../Booking'
import { filterBookingRatesToActivePlans } from '../Booking/filterBookingRatesToActivePlans'
import { isBookingDependentPlan } from './bookingDependentPricing'
import { getBookingCommissionPercentForPlan } from './getBookingCommissionPercentForPlan'
import { getBookingPlanNameGroupKey } from './groupBookingPlanRatesByName'
import { IPriceConfiguration } from './index'

export interface IFeeChangeSettingsData {
  booking: boolean
  bookingPrice: unknown
  bookingRates?: Record<string, unknown>
  airbnb: boolean
  airbnbPrice: unknown
}

export interface IFeeChangeRow {
  type: 'booking' | 'airbnb'
  planName: string
  oldDisplay: string | null
  newDisplay: string
}

export interface IFeeChangeRoom {
  roomName: string
  rows: IFeeChangeRow[]
}

interface IRoomLite {
  id: string | number
  name: string
}

interface IRoomSettingsGlobal {
  id?: string | number
  id_room?: string | number
  integrations?: {
    booking?: Array<{
      booking_id_pricing?: string | number
      active?: unknown
      percent?: unknown
      price?: unknown
    }>
    airbnb?: {
      active?: unknown
      percent?: unknown
      price?: unknown
    }
  }
}

function feePercentNumber(value: unknown): number {
  if (value === '' || value === undefined || value === null) return 0
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function feeBookingRatesNormalized(
  rates: Record<string, unknown> | undefined | null,
): Record<string, number> {
  if (!rates || typeof rates !== 'object') return {}
  const out: Record<string, number> = {}
  for (const k of Object.keys(rates).sort()) {
    out[k] = feePercentNumber(rates[k])
  }
  return out
}

function hasPerPlanBookingRates(
  rates: Record<string, unknown> | undefined | null,
): boolean {
  return !!rates && typeof rates === 'object' && Object.keys(rates).length > 0
}

function isBookingFeeSettingsChanged(
  config: IPriceConfiguration,
  data: IFeeChangeSettingsData,
): boolean {
  const lastRates =
    config.integrations?.booking?.rates &&
    typeof config.integrations.booking.rates === 'object'
      ? config.integrations.booking.rates
      : {}
  const nextRates =
    data.bookingRates && typeof data.bookingRates === 'object'
      ? data.bookingRates
      : {}

  const bookingRatesChanged = !Object.is(
    JSON.stringify(feeBookingRatesNormalized(lastRates as Record<string, unknown>)),
    JSON.stringify(feeBookingRatesNormalized(nextRates)),
  )

  return (
    (!config.integrations.booking.active && !!data.booking) ||
    (config.integrations.booking.active &&
      !!data.booking &&
      (feePercentNumber(config.integrations.booking.percent) !==
        feePercentNumber(data.bookingPrice) ||
        bookingRatesChanged))
  )
}

function isAirbnbFeeSettingsChanged(
  config: IPriceConfiguration,
  data: IFeeChangeSettingsData,
): boolean {
  return (
    (!config.integrations.airbnb.active && !!data.airbnb) ||
    (config.integrations.airbnb.active &&
      !!data.airbnb &&
      feePercentNumber(config.integrations.airbnb.percent) !==
        feePercentNumber(data.airbnbPrice))
  )
}

function formatGlobalCommissionPercent(value: number): string {
  return `${value}%`
}

function formatLocalCommissionDisplay(
  integration:
    | {
        active?: unknown
        percent?: unknown
        price?: unknown
      }
    | undefined,
  currency: string,
): string | null {
  if (!integration || !integration.active) return null
  const percent = feePercentNumber(integration.percent)
  const price = integration.price
  const hasPrice =
    price !== undefined &&
    price !== null &&
    price !== '' &&
    price !== 0 &&
    price !== '0' &&
    price !== '0.00'
  if (percent && !hasPrice) {
    return formatGlobalCommissionPercent(percent)
  }
  if (!percent && hasPrice) {
    return `${price} ${currency}`
  }
  if (percent && hasPrice) {
    return `${percent}% + ${price} ${currency}`
  }
  return null
}

function getNewBookingPlanPercent(
  planId: string,
  data: IFeeChangeSettingsData,
  perPlan: boolean,
): number {
  if (!data.booking) return 0
  if (
    perPlan &&
    data.bookingRates &&
    Object.prototype.hasOwnProperty.call(data.bookingRates, planId)
  ) {
    return feePercentNumber(data.bookingRates[planId])
  }
  return feePercentNumber(data.bookingPrice)
}

function bookingPlanHasCommissionChange(
  planId: string,
  config: IPriceConfiguration,
  data: IFeeChangeSettingsData,
  roomIntegration:
    | {
        active?: unknown
        percent?: unknown
        price?: unknown
      }
    | undefined,
  perPlan: boolean,
): boolean {
  if (!data.booking) return false

  const wasLocal = !!(roomIntegration && roomIntegration.active)
  const wasGlobal = config.integrations.booking.active && !wasLocal
  const wasIndependent = !config.integrations.booking.active && !wasLocal

  if (wasIndependent) return true
  if (wasLocal) return true

  if (wasGlobal) {
    const oldPercent = getBookingCommissionPercentForPlan(
      config.integrations.booking,
      planId,
      null,
    )
    const newPercent = getNewBookingPlanPercent(planId, data, perPlan)
    return feePercentNumber(oldPercent) !== feePercentNumber(newPercent)
  }

  return false
}

function airbnbHasCommissionChange(
  config: IPriceConfiguration,
  data: IFeeChangeSettingsData,
  roomIntegration:
    | {
        active?: unknown
        percent?: unknown
        price?: unknown
      }
    | undefined,
): boolean {
  if (!data.airbnb) return false

  const wasLocal = !!(roomIntegration && roomIntegration.active)
  const wasGlobal = config.integrations.airbnb.active && !wasLocal
  const wasIndependent = !config.integrations.airbnb.active && !wasLocal

  if (wasIndependent) return true
  if (wasLocal) return true

  if (wasGlobal) {
    return (
      feePercentNumber(config.integrations.airbnb.percent) !==
      feePercentNumber(data.airbnbPrice)
    )
  }

  return false
}

function getBookingPlanOldDisplay(
  planId: string,
  config: IPriceConfiguration,
  roomIntegration:
    | {
        active?: unknown
        percent?: unknown
        price?: unknown
      }
    | undefined,
  currency: string,
): string | null {
  const wasLocal = !!(roomIntegration && roomIntegration.active)
  if (wasLocal) {
    return formatLocalCommissionDisplay(roomIntegration, currency)
  }
  if (!config.integrations.booking.active) {
    return null
  }
  const percent = getBookingCommissionPercentForPlan(
    config.integrations.booking,
    planId,
    null,
  )
  return formatGlobalCommissionPercent(percent)
}

function getAirbnbOldDisplay(
  config: IPriceConfiguration,
  roomIntegration:
    | {
        active?: unknown
        percent?: unknown
        price?: unknown
      }
    | undefined,
  currency: string,
): string | null {
  const wasLocal = !!(roomIntegration && roomIntegration.active)
  if (wasLocal) {
    return formatLocalCommissionDisplay(roomIntegration, currency)
  }
  if (!config.integrations.airbnb.active) {
    return null
  }
  return formatGlobalCommissionPercent(
    feePercentNumber(config.integrations.airbnb.percent),
  )
}

/** Jedna pozycja na dokładną nazwę planu (case-sensitive), jak w FeeForm / modalach prowizji. */
function groupFeeChangeBookingRows(rows: IFeeChangeRow[]): IFeeChangeRow[] {
  const airbnbRows = rows.filter(row => row.type === 'airbnb')
  const bookingRows = rows.filter(row => row.type === 'booking')
  const byName = new Map<string, IFeeChangeRow[]>()

  for (const row of bookingRows) {
    const key = getBookingPlanNameGroupKey(row.planName)
    const group = byName.get(key) ?? []
    group.push(row)
    byName.set(key, group)
  }

  const groupedBooking = Array.from(byName.values())
    .map(groupRows => ({
      type: 'booking' as const,
      planName: groupRows[0].planName,
      oldDisplay: groupRows[0].oldDisplay,
      newDisplay: groupRows[0].newDisplay,
    }))
    .sort((a, b) => a.planName.localeCompare(b.planName))

  return [...groupedBooking, ...airbnbRows]
}

export function collectFeeChangeDialogRooms(
  config: IPriceConfiguration,
  data: IFeeChangeSettingsData,
  rooms: IRoomLite[],
  bookingObjects: IItemData[],
  roomSettingsGlobal: IRoomSettingsGlobal[],
  airbnbListings: Array<{ bedbooking_id_room?: string | number }>,
  currency: string,
): IFeeChangeRoom[] {
  const bookingChanged = isBookingFeeSettingsChanged(config, data)
  const airbnbChanged = isAirbnbFeeSettingsChanged(config, data)
  if (!bookingChanged && !airbnbChanged) {
    return []
  }

  const bookingRooms = bookingObjects.reduce(
    (acc, obj) => acc.concat(obj.rooms || []),
    [] as any[],
  )
  const bookingMapping = bookingObjects.reduce(
    (acc, obj) => acc.concat(obj.mappings || []),
    [] as any[],
  )
  const fullMappings = bookingMapping.map((mapping: any) => ({
    ...mapping,
    room: bookingRooms.find((room: any) => room.id === mapping.booking_id),
  }))

  const perPlan = hasPerPlanBookingRates(
    filterBookingRatesToActivePlans(
      (data.bookingRates ||
        config.integrations.booking.rates) as Record<string, unknown>,
      bookingObjects,
    ),
  )

  const result: IFeeChangeRoom[] = []

  for (const room of rooms) {
    const rows: IFeeChangeRow[] = []
    const roomSettings = roomSettingsGlobal.find(
      r =>
        String(r.id) === String(room.id) ||
        String(r.id_room) === String(room.id),
    )
    const bookingMappingForRoom = fullMappings.find((mapping: any) =>
      mapping.bed_booking_rooms.includes(String(room.id)),
    )
    const airbnbListing = airbnbListings.find(
      listing => String(listing.bedbooking_id_room) === String(room.id),
    )

    if (bookingChanged && bookingMappingForRoom && bookingMappingForRoom.room) {
      const pricing = (bookingMappingForRoom.room.pricing || []).filter(
        (plan: any) => plan && !plan.deleted && !isBookingDependentPlan(plan),
      )

      for (const plan of pricing) {
        const planId = String(plan.booking_id)
        const roomIntegration = roomSettings?.integrations?.booking?.find(
          b => String(b.booking_id_pricing) === planId,
        )

        if (
          !bookingPlanHasCommissionChange(
            planId,
            config,
            data,
            roomIntegration,
            perPlan,
          )
        ) {
          continue
        }

        rows.push({
          type: 'booking',
          planName: String(plan.name || planId),
          oldDisplay: getBookingPlanOldDisplay(
            planId,
            config,
            roomIntegration,
            currency,
          ),
          newDisplay: formatGlobalCommissionPercent(
            getNewBookingPlanPercent(planId, data, perPlan),
          ),
        })
      }
    }

    if (airbnbChanged && airbnbListing) {
      const roomIntegration = roomSettings?.integrations?.airbnb
      if (
        airbnbHasCommissionChange(config, data, roomIntegration)
      ) {
        rows.push({
          type: 'airbnb',
          planName: 'Airbnb',
          oldDisplay: getAirbnbOldDisplay(
            config,
            roomIntegration,
            currency,
          ),
          newDisplay: formatGlobalCommissionPercent(
            feePercentNumber(data.airbnbPrice),
          ),
        })
      }
    }

    if (rows.length > 0) {
      result.push({
        roomName: room.name,
        rows: groupFeeChangeBookingRows(rows),
      })
    }
  }

  return result
}

export function hasFeeCommissionDialogChanges(
  config: IPriceConfiguration,
  data: IFeeChangeSettingsData,
  rooms: IRoomLite[],
  bookingObjects: IItemData[],
  roomSettingsGlobal: IRoomSettingsGlobal[],
  airbnbListings: Array<{ bedbooking_id_room?: string | number }>,
  currency: string,
): boolean {
  return (
    collectFeeChangeDialogRooms(
      config,
      data,
      rooms,
      bookingObjects,
      roomSettingsGlobal,
      airbnbListings,
      currency,
    ).length > 0
  )
}
